import { productOnSale, productSalePrice } from '../productPricing';
import {
  hrefForSameSiteAsset,
  pathLooksLikeVideo,
  productGalleryImagePaths,
} from '../productMediaUtils';
import { describeJewelryCustomization, formatOrderItemCustomizationPlain } from '../jewelry/jewelryCustomization';
import {
  bespokeJewelryTypesLabel,
  bespokeOccasionLabel,
  bespokeStatusLabel,
} from '../jewelry/bespokeFormOptions';
import { parseBespokePhotos } from '../jewelry/customerPhotoUpload';
import { formatDelta, formatMoney, formatShortDate, paymentMethodLabel } from './analyticsUtils';

function toAbsoluteUrlForPrint(href) {
  const h = String(href || '').trim();
  if (!h || /^(data:|blob:)/i.test(h)) return h;
  if (/^https?:\/\//i.test(h)) return h;
  if (typeof window !== 'undefined' && window.location?.origin) {
    try {
      return new URL(h, window.location.origin).href;
    } catch {
      return `${window.location.origin}${h.startsWith('/') ? h : `/${h}`}`;
    }
  }
  return h;
}

/** Absolute image URL so print iframe / PDF can load photos (incl. dev upload server). */
export function productThumbUrlForPrint(p) {
  if (!p) return '';
  for (const u of productGalleryImagePaths(p)) {
    if (pathLooksLikeVideo(u)) continue;
    const url = toAbsoluteUrlForPrint(hrefForSameSiteAsset(u));
    if (url) return url;
  }
  return '';
}

function productImageCellHtml(p) {
  const src = productThumbUrlForPrint(p);
  if (!src) {
    return '<div class="no-img">No image</div>';
  }
  return `<img src="${escapeHtml(src)}" alt="" loading="eager" />`;
}

function customizationCellHtml(it) {
  const raw = it.customization_text;
  if (!raw) return '';
  const plain = formatOrderItemCustomizationPlain(raw);
  const jewelry = describeJewelryCustomization(raw);
  const photoHtml =
    jewelry?.photos?.length && jewelry.photos[0]?.url
      ? `<div class="slip-photo"><img src="${escapeHtml(
          toAbsoluteUrlForPrint(hrefForSameSiteAsset(jewelry.photos[0].url))
        )}" alt="Customer photo" /></div>`
      : '';
  return `<div class="custom">${escapeHtml(plain)}</div>${photoHtml}`;
}

/**
 * @param {object} order
 */
export function orderHasCustomerPhotos(order) {
  return (order?.items || []).some((it) => {
    const d = describeJewelryCustomization(it.customization_text);
    return Boolean(d?.photos?.length);
  });
}

/**
 * @param {object} order — full admin order detail
 */
export function buildCustomerPhotosPrintHtml(order) {
  const num = String(order?.order_number || '').trim();
  const entries = [];
  for (const it of order?.items || []) {
    const d = describeJewelryCustomization(it.customization_text);
    if (!d?.photos?.length) continue;
    for (const ph of d.photos) {
      const url = toAbsoluteUrlForPrint(hrefForSameSiteAsset(ph.url));
      if (!url) continue;
      entries.push({
        productName: String(it.product_name || 'Item'),
        fieldLabel: ph.fieldLabel || 'Customer photo',
        url,
      });
    }
  }
  if (!entries.length) return null;

  const pages = entries
    .map(
      (e) => `<section class="photo-page">
  <header class="photo-page__head">
    <h1>Saloni Designs — Customer photo</h1>
    <p class="photo-page__meta">Order #${escapeHtml(num)} · ${escapeHtml(e.productName)}</p>
    <p class="photo-page__field">${escapeHtml(e.fieldLabel)}</p>
    <p class="photo-page__when">Printed ${escapeHtml(new Date().toLocaleString('en-IN'))}</p>
  </header>
  <figure class="photo-page__figure">
    <img src="${escapeHtml(e.url)}" alt="${escapeHtml(e.fieldLabel)}" />
  </figure>
</section>`
    )
    .join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Customer photos — Order #${escapeHtml(num)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: "Segoe UI", system-ui, sans-serif; color: #111; background: #fff; }
  .print-hint { font-size: 9pt; color: #555; padding: 6mm 8mm 0; }
  .photo-page {
    min-height: 277mm;
    padding: 10mm;
    page-break-after: always;
    break-after: page;
    display: flex;
    flex-direction: column;
  }
  .photo-page:last-child { page-break-after: auto; break-after: auto; }
  .photo-page__head { margin-bottom: 8mm; }
  .photo-page__head h1 { font-size: 14pt; margin-bottom: 2mm; }
  .photo-page__meta { font-size: 11pt; font-weight: 600; }
  .photo-page__field, .photo-page__when { font-size: 10pt; color: #444; margin-top: 1mm; }
  .photo-page__figure { flex: 1; display: flex; align-items: center; justify-content: center; }
  .photo-page__figure img {
    max-width: 100%;
    max-height: 220mm;
    object-fit: contain;
    border: 1px solid #ccc;
  }
  @media print {
    .print-hint { display: none !important; }
    @page { size: A4 portrait; margin: 8mm; }
  }
</style></head><body>
<p class="print-hint">${entries.length} customer photo${entries.length === 1 ? '' : 's'} for order #${escapeHtml(num)}. One photo per page — use for locket / engraving work.</p>
${pages}
</body></html>`;
}

/** Print all customer-uploaded photos in an order (for workshop / locket embedding). */
export async function printOrderCustomerPhotos(order) {
  const html = buildCustomerPhotosPrintHtml(order);
  if (!html) {
    window.alert('This order has no customer photos to print.');
    return false;
  }
  const ok = await printHtmlDocument(html, { waitForImages: true });
  if (ok) return true;
  window.alert('Could not open print dialog. Allow pop-ups and try again.');
  return false;
}

function escCsv(v) {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}

function downloadTextFile(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function waitForImagesInDocument(doc, maxMs = 12000) {
  const imgs = doc ? [...doc.querySelectorAll('img')] : [];
  if (!imgs.length) return Promise.resolve();

  const loaded = new Promise((resolve) => {
    let pending = imgs.length;
    const tick = () => {
      pending -= 1;
      if (pending <= 0) resolve();
    };
    imgs.forEach((img) => {
      if (img.complete) tick();
      else {
        img.addEventListener('load', tick, { once: true });
        img.addEventListener('error', tick, { once: true });
      }
    });
  });

  const timeout = new Promise((resolve) => {
    window.setTimeout(resolve, maxMs);
  });

  return Promise.race([loaded, timeout]);
}

function printHtmlDocument(html, { waitForImages = false } = {}) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Print');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:0;top:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;';

    let settled = false;
    let failTimer = 0;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(failTimer);
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
      resolve(ok);
    };

    iframe.onload = () => {
      window.setTimeout(async () => {
        try {
          const win = iframe.contentWindow;
          const doc = win?.document;
          if (waitForImages && doc) {
            await waitForImagesInDocument(doc);
          }
          win?.focus();
          win?.print();
          finish(true);
        } catch {
          finish(false);
        }
      }, 200);
    };
    iframe.onerror = () => finish(false);
    failTimer = window.setTimeout(() => finish(false), 20000);

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

const PRINT_PAGE_CSS = `
  * { box-sizing: border-box; }
  body { font-family: "Segoe UI", system-ui, sans-serif; color: #111; font-size: 11pt; margin: 12mm; }
  h1 { font-size: 16pt; margin: 0 0 4px; }
  .meta { color: #444; font-size: 9pt; margin-bottom: 12px; }
  h2 { font-size: 11pt; margin: 14px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 9pt; }
  th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #f5f5f5; }
  td.num, th.num { text-align: right; }
  .kpi { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
  .kpi div { border: 1px solid #ddd; padding: 6px 10px; border-radius: 4px; min-width: 120px; }
  .kpi strong { display: block; font-size: 12pt; }
  .kpi span { font-size: 8pt; color: #555; }
  @media print { body { margin: 10mm; } }
`;

const PRODUCTS_PRINT_CSS = `
  ${PRINT_PAGE_CSS}
  body.products-print { font-size: 10pt; }
  .products-print table { font-size: 9pt; }
  .products-print tr { page-break-inside: avoid; }
  .products-print td.img-cell { width: 112px; padding: 8px; vertical-align: middle; }
  .products-print td.img-cell img {
    width: 96px;
    height: 96px;
    object-fit: cover;
    object-position: center;
    display: block;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: #fff;
  }
  .products-print .no-img {
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f0f0;
    border: 1px dashed #ccc;
    border-radius: 6px;
    font-size: 8pt;
    color: #777;
    text-align: center;
    padding: 4px;
  }
  .products-print .name-cell { font-weight: 600; min-width: 10rem; }
  @media print {
    .products-print td.img-cell img,
    .products-print .no-img {
      width: 96px;
      height: 96px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

/**
 * Export full catalog to CSV (opens in Excel).
 */
export function exportProductsCsv(sortedCategories, editableProducts, { categorySlug = null } = {}) {
  const headers = [
    'Category',
    'Category slug',
    'Product ID',
    'Name',
    'Type',
    'Subcategory',
    'Price INR',
    'Sale price INR',
    'On sale',
    'Stock qty',
    'Visible',
    'Image URL',
  ];
  const lines = [headers.map(escCsv).join(',')];

  const cats = (sortedCategories || []).filter((c) => !categorySlug || c.slug === categorySlug);

  for (const cat of cats) {
    const slug = cat.slug;
    const list = editableProducts[slug] || [];
    for (const p of list) {
      const sq = p.stock_qty;
      const stock =
        sq === null || sq === undefined ? 'unlimited' : String(Math.max(0, Math.floor(Number(sq) || 0)));
      lines.push(
        [
          cat.name,
          slug,
          p.id,
          p.name,
          p.type || '',
          p.subcategory || '',
          p.price,
          productOnSale(p) ? productSalePrice(p) : '',
          productOnSale(p) ? 'yes' : 'no',
          stock,
          p.visible === false ? 'no' : 'yes',
          productThumbUrlForPrint(p),
        ]
          .map(escCsv)
          .join(',')
      );
    }
  }

  const suffix = categorySlug ? `-${categorySlug}` : '-all';
  downloadTextFile(lines.join('\r\n'), `saloni-products${suffix}.csv`, 'text/csv;charset=utf-8');
}

/**
 * Print current product table (pass pre-filtered rows).
 */
export async function printProductsList({
  categoryName,
  products,
  searchLabel,
  stockFilterLabel,
}) {
  const rows = (products || [])
    .map(({ p }) => p)
    .filter(Boolean);

  const bodyRows = rows
    .map((p) => {
      const sq = p.stock_qty;
      const stock =
        sq === null || sq === undefined ? '∞' : String(Math.max(0, Math.floor(Number(sq) || 0)));
      const price = productOnSale(p)
        ? `${formatMoney(productSalePrice(p))} (was ${formatMoney(p.price)})`
        : formatMoney(p.price);
      return `<tr>
        <td class="img-cell">${productImageCellHtml(p)}</td>
        <td class="name-cell">${escapeHtml(p.name)}</td>
        <td><code>${escapeHtml(p.id)}</code></td>
        <td>${escapeHtml(p.type || '—')}</td>
        <td>${escapeHtml(p.subcategory || '—')}</td>
        <td class="num">${escapeHtml(stock)}</td>
        <td class="num">${escapeHtml(price)}</td>
      </tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Products</title>
<style>${PRODUCTS_PRINT_CSS}</style></head><body class="products-print">
<h1>Saloni Designs — Product catalog</h1>
<p class="meta">Category: ${escapeHtml(categoryName || '—')} · ${rows.length} product(s)
${searchLabel ? ` · Search: ${escapeHtml(searchLabel)}` : ''}
${stockFilterLabel ? ` · Filter: ${escapeHtml(stockFilterLabel)}` : ''}
<br/>Printed ${escapeHtml(new Date().toLocaleString('en-IN'))}</p>
<table>
<thead><tr><th>Photo</th><th>Name</th><th>ID</th><th>Type</th><th>Subcategory</th><th class="num">Stock</th><th class="num">Price</th></tr></thead>
<tbody>${bodyRows || '<tr><td colspan="7">No products</td></tr>'}</tbody>
</table>
<p class="meta">Images print at full quality — PDF may be large. Use Print → Save as PDF.</p>
</body></html>`;

  const ok = await printHtmlDocument(html, { waitForImages: true });
  if (!ok) window.alert('Could not open print dialog. Allow pop-ups and try again.');
}

/**
 * Print analytics report (Save as PDF from print dialog).
 */
export async function printAnalyticsReport(data, rangeLabel) {
  if (!data) return;

  const period = data.period || {};
  const cmp = data.comparison || {};
  const revDelta = formatDelta(cmp.revenue_pct);

  const table = (title, headers, rows, numericCols = []) => {
    if (!rows?.length) return '';
    const head = headers.map((h) => `<th${numericCols.includes(h) ? ' class="num"' : ''}>${escapeHtml(h)}</th>`).join('');
    const body = rows
      .map((row) => {
        const cells = headers
          .map((h) => {
            const v = row[h];
            const isNum = numericCols.includes(h);
            return `<td${isNum ? ' class="num"' : ''}>${escapeHtml(v)}</td>`;
          })
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    return `<h2>${escapeHtml(title)}</h2><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  };

  const dailyRows = (data.daily || []).map((d) => ({
    Date: formatShortDate(d.date),
    Revenue: formatMoney(d.revenue),
    Orders: d.orders,
  }));

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Analytics</title>
<style>${PRINT_PAGE_CSS}</style></head><body>
<h1>Saloni Designs — Shop analytics</h1>
<p class="meta">${escapeHtml(rangeLabel)} · Generated ${escapeHtml(new Date().toLocaleString('en-IN'))}</p>
<div class="kpi">
  <div><span>Revenue</span><strong>${escapeHtml(formatMoney(period.revenue))}</strong><span>${escapeHtml(revDelta.text)} vs prior</span></div>
  <div><span>Paid orders</span><strong>${escapeHtml(String(period.orders ?? 0))}</strong></div>
  <div><span>AOV</span><strong>${escapeHtml(formatMoney(period.aov))}</strong></div>
  <div><span>Units sold</span><strong>${escapeHtml(String(period.units_sold ?? 0))}</strong></div>
  <div><span>Repeat rate</span><strong>${escapeHtml(String(data.customers_period?.repeat_rate_pct ?? 0))}%</strong></div>
</div>
${table('Daily trend', ['Date', 'Revenue', 'Orders'], dailyRows, ['Orders'])}
${table(
  'Top products',
  ['Product', 'Category', 'Qty', 'Revenue'],
  (data.top_products || []).map((p) => ({
    Product: p.product_name,
    Category: p.category_slug,
    Qty: p.quantity,
    Revenue: formatMoney(p.revenue),
  })),
  ['Qty']
)}
${table(
  'Top categories',
  ['Category', 'Qty', 'Revenue'],
  (data.top_categories || []).map((c) => ({
    Category: c.category_slug,
    Qty: c.quantity,
    Revenue: formatMoney(c.revenue),
  })),
  ['Qty']
)}
${table(
  'Top cities',
  ['City', 'Orders', 'Revenue'],
  (data.top_cities || []).map((c) => ({
    City: c.city,
    Orders: c.orders,
    Revenue: formatMoney(c.revenue),
  })),
  ['Orders']
)}
${table(
  'Payment mix',
  ['Method', 'Orders', 'Revenue'],
  (data.payment_methods || []).map((m) => ({
    Method: paymentMethodLabel(m.method),
    Orders: m.orders,
    Revenue: formatMoney(m.revenue),
  })),
  ['Orders']
)}
${(() => {
  const op = data.online_payments || {};
  if (!op || !(op.pending_count || op.period_paid_razorpay_orders)) return '';
  return table('Razorpay summary', ['Metric', 'Value'], [
    { Metric: 'Awaiting Razorpay payment (open)', Value: op.pending_count ?? 0 },
    { Metric: 'Awaiting payment value', Value: formatMoney(op.pending_value ?? 0) },
    { Metric: 'Paid with Razorpay id (period)', Value: op.period_paid_razorpay_orders ?? 0 },
    { Metric: 'Razorpay paid revenue (period)', Value: formatMoney(op.period_paid_razorpay_revenue ?? 0) },
  ]);
})()}
<p class="meta">Tip: In the print dialog, choose &ldquo;Save as PDF&rdquo; for a PDF file. For Excel, use Export CSV on the analytics screen. Razorpay payment ids per order: export Orders CSV.</p>
</body></html>`;

  const ok = await printHtmlDocument(html);
  if (!ok) window.alert('Could not open print dialog. Allow pop-ups and try again.');
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN');
  } catch {
    return String(iso);
  }
}

function csvFromRows(headers, rows) {
  const lines = [headers.map(escCsv).join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => escCsv(row[h])).join(','));
  }
  return lines.join('\r\n');
}

/** Export current orders list (summary rows). */
export function exportOrdersSummaryCsv(
  orders,
  { statusLabel = '', searchLabel = '', customerLabel = '' } = {}
) {
  const headers = [
    'Order #',
    'Status',
    'Total',
    'Created',
    'Paid at',
    'Payment',
    'Payment method',
    'Paid with',
    'Razorpay order id',
    'Razorpay payment id',
    'Ship to',
    'City',
    'Pincode',
    'Customer',
    'Username',
    'Email',
    'Phone',
  ];
  const rows = (orders || []).map((o) => ({
    'Order #': o.order_number,
    Status: o.status,
    Total: o.total_amount,
    Created: o.created_at,
    'Paid at': o.paid_at || '',
    Payment: o.payment_type || '',
    'Payment method': o.payment_method || '',
    'Paid with': o.payment_instrument || '',
    'Razorpay order id': o.razorpay_order_id || '',
    'Razorpay payment id': o.razorpay_payment_id || '',
    'Ship to': o.shipping_name || '',
    City: o.shipping_city || '',
    Pincode: o.shipping_pincode || '',
    Customer: o.customer?.display_name || '',
    Username: o.customer?.username || '',
    Email: o.customer?.email || '',
    Phone: o.customer?.phone || '',
  }));
  const suffix = [statusLabel, customerLabel, searchLabel].filter(Boolean).join('-') || 'filtered';
  downloadTextFile(
    csvFromRows(headers, rows),
    `saloni-orders-${suffix.replace(/\s+/g, '-')}.csv`,
    'text/csv;charset=utf-8'
  );
}

/** Fetch line-item rows for current filters and download CSV. */
export async function fetchAndExportOrdersLinesCsv(
  sessionToken,
  { statusFilter = '', customerFilter = '', search = '', adminAuthHeaders: authHeaders }
) {
  const params = new URLSearchParams({ export: 'lines', limit: '200' });
  if (statusFilter) params.set('status', statusFilter);
  if (customerFilter) params.set('customer_id', customerFilter);
  if (search.trim()) params.set('q', search.trim());
  const r = await fetch(`/api/admin_orders.php?${params}`, {
    headers: authHeaders(sessionToken),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.success) {
    window.alert(d.error || 'Could not export order lines.');
    return;
  }
  const headers = [
    'Order #',
    'Status',
    'Created',
    'Paid at',
    'Total',
    'Payment',
    'Payment method',
    'Paid with',
    'Razorpay order id',
    'Razorpay payment id',
    'Ship to',
    'City',
    'Pincode',
    'Customer',
    'Username',
    'Email',
    'Phone',
    'Product',
    'Product ID',
    'Category',
    'Qty',
    'Unit price',
    'Line total',
    'Personalization',
  ];
  const rows = (d.rows || []).map((row) => ({
    'Order #': row.order_number,
    Status: row.status,
    Created: row.created_at,
    'Paid at': row.paid_at || '',
    Total: row.total_amount,
    Payment: row.payment_type,
    'Payment method': row.payment_method || '',
    'Paid with': row.payment_instrument || '',
    'Razorpay order id': row.razorpay_order_id || '',
    'Razorpay payment id': row.razorpay_payment_id || '',
    'Ship to': row.shipping_name,
    City: row.shipping_city,
    Pincode: row.shipping_pincode,
    Customer: row.customer_display_name,
    Username: row.customer_username,
    Email: row.customer_email,
    Phone: row.customer_phone,
    Product: row.product_name,
    'Product ID': row.product_external_id,
    Category: row.category_slug,
    Qty: row.quantity,
    'Unit price': row.unit_price,
    'Line total': row.line_total,
    Personalization: formatOrderItemCustomizationPlain(row.customization_text),
  }));
  downloadTextFile(
    csvFromRows(headers, rows),
    'saloni-orders-with-lines.csv',
    'text/csv;charset=utf-8'
  );
}

/** Max compact packing slips per A4 (orders with many lines print one per page). */
export const PACKING_SLIPS_PER_A4_PAGE = 2;
const PACKING_SLIP_LARGE_ITEM_COUNT = 6;

const PACKING_SLIP_BASE_CSS = `
  .muted, .custom { color: #555; font-size: 7pt; margin-top: 1px; line-height: 1.25; }
  .slip-photo { margin-top: 2px; }
  .slip-photo img { max-width: 28mm; max-height: 28mm; object-fit: contain; border: 1px solid #ccc; }
  .slip-head { margin-bottom: 3mm; }
  .slip-head h1 { font-size: 11pt; margin: 0 0 2px; }
  .slip-head .meta { font-size: 7.5pt; margin: 0; color: #444; }
  .slip-cols { display: flex; gap: 4mm; margin-bottom: 3mm; font-size: 8pt; line-height: 1.35; }
  .slip-cols > div { flex: 1 1 0; min-width: 0; }
  .slip-cols h2 { font-size: 8pt; margin: 0 0 2px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
  .slip-items { width: 100%; border-collapse: collapse; font-size: 7.5pt; margin-bottom: 2mm; }
  .slip-items th, .slip-items td { border: 1px solid #ddd; padding: 2px 4px; vertical-align: top; }
  .slip-items th { background: #f5f5f5; }
  .slip-items .num { text-align: right; }
  .slip-totals { width: 100%; font-size: 8pt; }
  .slip-totals td { border: none; padding: 1px 4px; }
  .slip-totals .num { text-align: right; }
  .slip-foot { font-size: 7pt; color: #444; margin-top: 2mm; }
`;

function packingSlipItemCount(order) {
  return (order.items || []).length;
}

/** Group orders onto A4 pages (2 compact slips, or 1 if many line items). */
export function paginatePackingSlips(orders) {
  const pages = [];
  let i = 0;
  const list = orders || [];
  while (i < list.length) {
    const order = list[i];
    const large = packingSlipItemCount(order) > PACKING_SLIP_LARGE_ITEM_COUNT;
    if (large) {
      pages.push([order]);
      i += 1;
      continue;
    }
    const next = list[i + 1];
    if (next && packingSlipItemCount(next) <= PACKING_SLIP_LARGE_ITEM_COUNT) {
      pages.push([order, next]);
      i += 2;
    } else {
      pages.push([order]);
      i += 1;
    }
  }
  return pages;
}

/**
 * @param {object} order
 * @param {{ compact?: boolean, showPrintedAt?: boolean }} [opts]
 */
export function buildPackingSlipInnerHtml(order, opts = {}) {
  const compact = Boolean(opts.compact);
  const showPrintedAt = opts.showPrintedAt !== false;
  const ship = order.shipping || {};
  const cust = order.customer || {};
  const itemsHtml = (order.items || [])
    .map(
      (it) => `<tr>
        <td>${escapeHtml(it.product_name)}${it.customization_text ? customizationCellHtml(it) : ''}</td>
        <td><code>${escapeHtml(it.product_external_id)}</code></td>
        <td class="num">${escapeHtml(String(it.quantity))}</td>
        <td class="num">${escapeHtml(formatMoney(it.unit_price))}</td>
        <td class="num">${escapeHtml(formatMoney(it.line_total))}</td>
      </tr>`
    )
    .join('');

  const discount =
    order.discount_amount > 0
      ? `<tr><td colspan="4" class="text-end">Discount${order.coupon_code ? ` (${escapeHtml(order.coupon_code)})` : ''}</td><td class="num">−${escapeHtml(formatMoney(order.discount_amount))}</td></tr>`
      : '';

  const printedLine = showPrintedAt
    ? `<br/>Printed ${escapeHtml(new Date().toLocaleString('en-IN'))}`
    : '';

  const slipClass = compact ? 'packing-slip packing-slip--compact' : 'packing-slip packing-slip--solo';

  return `
  <div class="${slipClass}">
    <div class="slip-head">
      <h1>Saloni Designs — Packing slip</h1>
      <p class="meta">Order #${escapeHtml(order.order_number)} · ${escapeHtml(formatWhen(order.created_at))}
 · ${escapeHtml(order.status || '')}${order.paid_at ? ` · Paid ${escapeHtml(formatWhen(order.paid_at))}` : ''}${printedLine}</p>
    </div>
    <div class="slip-cols">
      <div>
        <h2>Deliver to</h2>
        <p><strong>${escapeHtml(ship.name)}</strong><br/>
        ${escapeHtml(ship.phone)}<br/>
        ${escapeHtml(ship.address)}<br/>
        ${escapeHtml([ship.city, ship.state].filter(Boolean).join(', '))} ${escapeHtml(ship.pincode || '')}</p>
      </div>
      <div>
        <h2>Customer</h2>
        <p>${escapeHtml(cust.display_name || cust.username || '—')}<br/>
        ${cust.email ? `${escapeHtml(cust.email)}<br/>` : ''}
        ${cust.phone ? escapeHtml(cust.phone) : ''}</p>
      </div>
    </div>
    <table class="slip-items">
      <thead><tr><th>Product</th><th>ID</th><th class="num">Qty</th><th class="num">Unit</th><th class="num">Line</th></tr></thead>
      <tbody>${itemsHtml || '<tr><td colspan="5">No items</td></tr>'}</tbody>
    </table>
    <table class="slip-totals">
      <tr><td colspan="4" class="text-end">Subtotal</td><td class="num">${escapeHtml(formatMoney(order.subtotal))}</td></tr>
      <tr><td colspan="4" class="text-end">Shipping</td><td class="num">${escapeHtml(formatMoney(order.shipping_amount))}</td></tr>
      ${discount}
      <tr><td colspan="4" class="text-end"><strong>Total</strong></td><td class="num"><strong>${escapeHtml(formatMoney(order.total_amount))}</strong></td></tr>
    </table>
    ${order.customer_notes ? `<p class="slip-foot"><strong>Note:</strong> ${escapeHtml(order.customer_notes)}</p>` : ''}
    ${order.tracking_number ? `<p class="slip-foot"><strong>Tracking:</strong> ${escapeHtml(order.tracking_carrier || '')} ${escapeHtml(order.tracking_number)}</p>` : ''}
  </div>`;
}

function packingSlipsA4Css() {
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Segoe UI", system-ui, sans-serif;
      color: #111;
      background: #fff;
    }
    .print-hint {
      font-size: 9pt;
      color: #555;
      padding: 6mm 8mm 0;
    }
    .slips-a4-sheet {
      display: flex;
      flex-direction: column;
      gap: 4mm;
      min-height: 277mm;
      padding: 0 0 4mm;
      page-break-after: always;
      break-after: page;
    }
    .slips-a4-sheet:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .packing-slip {
      flex: 1 1 0;
      border: 1px solid #bbb;
      padding: 4mm;
      overflow: hidden;
    }
    .packing-slip--solo {
      flex: 1 1 auto;
      min-height: 0;
    }
    .packing-slip--compact .slip-head h1 { font-size: 10pt; }
    ${PACKING_SLIP_BASE_CSS}
    @media print {
      .print-hint { display: none !important; }
      @page { size: A4 portrait; margin: 8mm; }
      .slips-a4-sheet { min-height: 277mm; }
    }
  `;
}

/**
 * @param {object[]} orders — full order detail objects
 */
export function buildPackingSlipsA4Html(orders) {
  const valid = (orders || []).filter((o) => o?.order_number);
  if (!valid.length) return null;

  const pages = paginatePackingSlips(valid);
  const sheetsHtml = pages
    .map((pageOrders) => {
      const slips = pageOrders
        .map((o) => {
          const solo = pageOrders.length === 1;
          return buildPackingSlipInnerHtml(o, {
            compact: !solo,
            showPrintedAt: solo,
          });
        })
        .join('');
      return `<div class="slips-a4-sheet">${slips}</div>`;
    })
    .join('');

  const slipCount = valid.length;
  const pageCount = pages.length;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Packing slips (${slipCount})</title>
<style>${packingSlipsA4Css()}</style></head><body>
<p class="print-hint">Packing slips: ${slipCount} order${slipCount === 1 ? '' : 's'} · ${pageCount} A4 page${pageCount === 1 ? '' : 's'} (up to ${PACKING_SLIPS_PER_A4_PAGE} per page when the order has ${PACKING_SLIP_LARGE_ITEM_COUNT} items or fewer). Choose A4 in the print dialog.</p>
${sheetsHtml}
</body></html>`;
}

/** Print one or more packing slips (A4, multiple per page when compact). */
export async function printPackingSlipsA4(orders) {
  const html = buildPackingSlipsA4Html(orders);
  if (!html) {
    window.alert('No orders to print.');
    return false;
  }

  const ok = await printHtmlDocument(html, { waitForImages: false });
  if (ok) return true;

  window.alert('Could not open print dialog. Allow pop-ups and try again.');
  return false;
}

/** Print packing slip for one order (admin). */
export async function printOrderPackingSlip(order) {
  if (!order?.order_number) return;
  await printPackingSlipsA4([order]);
}

export function exportCustomersCsv(customers) {
  const headers = [
    'Username',
    'Display name',
    'Email',
    'Phone',
    'Joined',
    'Orders',
    'Last order',
    'Last order #',
    'Address label',
    'Address name',
    'Address phone',
    'Address line',
    'City',
    'State',
    'Pincode',
  ];
  const rows = (customers || []).map((c) => {
    const a = c.default_address || {};
    return {
      Username: c.username,
      'Display name': c.display_name || '',
      Email: c.email || '',
      Phone: c.phone || '',
      Joined: c.created_at,
      Orders: c.order_count ?? 0,
      'Last order': c.last_order_at || '',
      'Last order #': c.last_order_number || '',
      'Address label': a.label || '',
      'Address name': a.name || '',
      'Address phone': a.phone || '',
      'Address line': a.address || '',
      City: a.city || '',
      State: a.state || '',
      Pincode: a.pincode || '',
    };
  });
  downloadTextFile(csvFromRows(headers, rows), 'saloni-customers.csv', 'text/csv;charset=utf-8');
}

export function exportCouponsCsv(coupons) {
  const headers = [
    'Code',
    'Description',
    'Type',
    'Value',
    'Max discount',
    'Min subtotal',
    'Audience',
    'Restricted user id',
    'Restricted email',
    'First order only',
    'Max uses total',
    'Max uses per user',
    'Starts',
    'Expires',
    'Uses',
    'Active',
  ];
  const rows = (coupons || []).map((c) => ({
    Code: c.code,
    Description: c.description || '',
    Type: c.discount_type,
    Value: c.discount_value,
    'Max discount': c.max_discount ?? '',
    'Min subtotal': c.min_subtotal,
    Audience: c.restricted_user_id
      ? 'customer'
      : c.restricted_email
        ? 'email'
        : 'all',
    'Restricted user id': c.restricted_user_id ?? '',
    'Restricted email': c.restricted_email || '',
    'First order only': c.first_order_only ? 'yes' : 'no',
    'Max uses total': c.max_uses_total ?? '',
    'Max uses per user': c.max_uses_per_user ?? '',
    Starts: c.starts_at ? String(c.starts_at).slice(0, 10) : '',
    Expires: c.expires_at ? String(c.expires_at).slice(0, 10) : '',
    Uses: c.usage_count ?? 0,
    Active: c.is_active ? 'yes' : 'no',
  }));
  downloadTextFile(csvFromRows(headers, rows), 'saloni-coupons.csv', 'text/csv;charset=utf-8');
}

export function exportReviewsCsv(reviews) {
  const headers = [
    'ID',
    'Product',
    'Product ID',
    'Category',
    'Author',
    'Rating',
    'Approved',
    'Created',
    'Review',
  ];
  const rows = (reviews || []).map((r) => ({
    ID: r.id,
    Product: r.product_name,
    'Product ID': r.product_external_id,
    Category: r.category_slug,
    Author: r.author_name,
    Rating: r.rating,
    Approved: r.is_approved ? 'yes' : 'no',
    Created: r.created_at,
    Review: (r.body || '').replace(/\s+/g, ' ').trim(),
  }));
  downloadTextFile(csvFromRows(headers, rows), 'saloni-reviews.csv', 'text/csv;charset=utf-8');
}

export function exportGiftFinderCsv(data, rangeLabel = '') {
  if (!data?.available) {
    window.alert(data?.message || 'Gift finder analytics not available.');
    return;
  }

  const sections = [];
  const pushSection = (title, headers, rows) => {
    sections.push(title);
    sections.push(headers.map(escCsv).join(','));
    for (const row of rows) {
      sections.push(headers.map((h) => escCsv(row[h])).join(','));
    }
    sections.push('');
  };

  pushSection('Summary', ['Metric', 'Value'], [
    { Metric: 'Range', Value: rangeLabel },
    { Metric: 'Sessions', Value: data.sessions ?? 0 },
    { Metric: 'Completed sessions', Value: data.completed_sessions ?? 0 },
    { Metric: 'Completion rate %', Value: data.completion_rate_pct ?? 0 },
    { Metric: 'Result views', Value: data.results?.views ?? 0 },
    { Metric: 'Avg products per result', Value: data.results?.avg_result_count ?? 0 },
    { Metric: 'Empty result views', Value: data.results?.zero_result_views ?? 0 },
  ]);

  const funnelSteps = ['recipient', 'collection', 'group', 'budget', 'results'];
  pushSection(
    'Funnel',
    ['Step', 'Views', 'Selections'],
    funnelSteps.map((step) => {
      const row = data.funnel?.[step] || {};
      return { Step: step, Views: row.views ?? 0, Selections: row.selects ?? 0 };
    })
  );

  pushSection(
    'Top product clicks',
    ['Product ID', 'Clicks'],
    (data.top_product_clicks || []).map((r) => ({
      'Product ID': r.product_id,
      Clicks: r.clicks,
    }))
  );

  pushSection(
    'Popular filter combos',
    ['Category', 'Group', 'Budget', 'Count'],
    (data.top_filter_combos || []).map((r) => ({
      Category: r.category || '',
      Group: r.group || '',
      Budget: r.budget || '',
      Count: r.count,
    }))
  );

  downloadTextFile(sections.join('\r\n'), 'saloni-gift-finder-analytics.csv', 'text/csv;charset=utf-8');
}

export function exportBespokeRequestsCsv(requests, storeCopy) {
  const headers = [
    'Request #',
    'Status',
    'Name',
    'Email',
    'Phone',
    'Occasion',
    'Jewelry type',
    'Event date',
    'Dress photo URL',
    'Reference photo URL',
    'Customer notes',
    'Admin notes',
    'Created',
    'Updated',
  ];
  const rows = (requests || []).map((r) => ({
    'Request #': r.request_number || '',
    Status: bespokeStatusLabel(r.status),
    Name: r.name || '',
    Email: r.email || '',
    Phone: r.phone || '',
    Occasion: bespokeOccasionLabel(r.occasion, storeCopy),
    'Jewelry type': bespokeJewelryTypesLabel(r.jewelry_type, storeCopy),
    'Event date': r.event_date || '',
    'Dress photo URL': parseBespokePhotos(r.dress_photo).join('; '),
    'Reference photo URL': parseBespokePhotos(r.reference_photo).join('; '),
    'Customer notes': r.notes || '',
    'Admin notes': r.admin_notes || '',
    Created: r.created_at || '',
    Updated: r.updated_at || '',
  }));
  downloadTextFile(csvFromRows(headers, rows), 'saloni-custom-jewelry-requests.csv', 'text/csv;charset=utf-8');
}
